/* eslint-disable max-lines */
import {trim} from 'lodash';
import {isNullOrUndefined} from 'common/util/HelperUtil';
import {ValidatorUtil} from 'common/util/ValidatorUtil';
import diff from 'fast-diff';
import {StringUtil} from 'common/util/StringUtil';
import {FormatterUtil} from 'common/util/FormatterUtil';

export class AddressUtil {
    private static readonly ZIPCODE_CITY: { [zipCode: string]: string[] } = {
        '78500': ['Sartrouville'], '78600': ['Maisons-Laffitte', 'Le Mesnil-le-Roi'], '78800': ['Houilles'], '95240': ['Cormeilles-en-Parisis'], '95530': ['La Frette-sur-Seine'],
        '78100': ['Saint-Germain-en-Laye'], '95100': ['Argenteuil'],
        '78630': ['Orgeval', 'Morainvilliers'], '78670': ['Villennes-sur-Seine', 'Medan'], '78240': ['Chambourcy', 'Aigremont'], '78300': ['Poissy'], '78510': ['Triel-sur-Seine'],
        '78570': ['Andrésy', 'Chanteloup-les-Vignes'], '78700': ['Conflans-Sainte-Honorine'], '78480': ['Verneuil-sur-Seine'], '78750': ['Mareil-Marly'], '78620': ['L\'Étang-la-Ville'],
        '78160': ['Marly-le-Roi'], '78580': ['Les Alluets-le-Roi'],
        '78955': ['Carrières-sous-Poissy'], '78260': ['Achères'], '78540': ['Vernouillet'], '78920': ['Ecquevilly'],
        '95150': ['Taverny'], '95250': ['Beauchamp'], '95480': ['Pierrelaye'], '95320': ['Saint-Leu-la-Forêt'], '95550': ['Bessancourt'], '95370': ['Montigny-lès-Cormeilles'], '95220': ['Herblay'],
        '95740': ['Frépillon'], '95540': ['Méry-sur-Oise'],
        '95870': ['Bezons'], '78420': ['Carrières-sur-Seine'], '78400': ['Chatou'], '78110': ['Le Vésinet'], '78360': ['Montesson'], '78290': ['Croissy-sur-Seine', 'Croissy'], '78230': ['Le Pecq'],
        '95600': ['Eaubonne']
    };
    private static readonly REGIONS: string[] = ['île-de-france'];
    private static readonly COUNTRIES: string[] = ['France', 'FR'];
    private static readonly STREET_TYPE: { short: string, long: string }[] = [
        {short: 'av', long: 'avenue'},
        {short: 'rue', long: 'rue'},
        {short: 'bd', long: 'boulevard'},
        {short: 'chau', long: 'chaussée'},
        {short: 'trav', long: 'traverse'},
        {short: 'pl', long: 'place'},
        {short: 'pass', long: 'passage'},
        {short: 'sent', long: 'sentier'},
        {short: 'imp', long: 'impasse'},
        {short: 'all', long: 'allée'},
        {short: 'chem', long: 'chemin'},
        {short: 'ham', long: 'hameau'},
        {short: 'dom', long: 'domaine'}
    ];

    public static autoCorrectCity(city: string): string {
        if (isNullOrUndefined(city) || typeof city !== 'string' || city.length === 0) {
            return city;
        }

        if (city.toLowerCase().startsWith('st-') || city.toLowerCase().startsWith('st ')) {
            city = 'Saint ' + city.substring(3);
        }

        const CITIES: string[] = Object.values(AddressUtil.ZIPCODE_CITY).flat();

        const exactMatch: string = CITIES.find((c) => c.toLowerCase() === city.toLowerCase());
        if (exactMatch !== undefined) {
            return exactMatch;
        }

        const partialMatch: string = CITIES.find((c) => {
            const result: { add: number, remove: number } = AddressUtil.countDiff(city, c);

            return result.add <= 2 && result.remove <= 2;
        });
        if (partialMatch !== undefined) {
            return partialMatch;
        }

        const matchNoDash: string = CITIES.find((c) => c.replace(/-/g, ' ').toLowerCase() === city.toLowerCase());
        if (matchNoDash !== undefined) {
            return matchNoDash;
        }

        const partialMatchNoDash: string = CITIES.find((c) => {
            const result: { add: number, remove: number } = AddressUtil.countDiff(city, c.replace(/-/g, ' '));

            return result.add <= 2 && result.remove <= 2;
        });
        if (partialMatchNoDash !== undefined) {
            return partialMatchNoDash;
        }

        const startsWith: string = CITIES.find((c) => c.toLowerCase().startsWith(city.toLowerCase()));
        if (startsWith !== undefined) {
            return startsWith;
        }

        const isIncluded: string = CITIES.find((c) => city.toLowerCase().includes(c.toLowerCase()));
        if (isIncluded !== undefined) {
            return isIncluded;
        }

        return city;
    }

    public static autoCorrectStreet(street: string): string {
        if (isNullOrUndefined(street) || typeof street !== 'string' || street.length === 0) {
            return street;
        }

        AddressUtil.STREET_TYPE.forEach((type) => {
            if (type.short.length < type.long.length) {
                street = street.replace(new RegExp('(\\s' + String(type.short) + '\\.?\\s)', 'ig'), ' ' + String(type.long) + ' ');
                street = street.replace(new RegExp('(\\s' + String(type.short) + '\\.\\s)', 'ig'), ' ' + String(type.long) + ' ');
            }
        });
        // street = street.replace(/(\sav\s)/ig, ' avenue ');
        // street = street.replace(/(\sav\.\s)/ig, ' avenue ');
        // street = street.replace(/(\sbd\s)/ig, ' boulevard ');
        // street = street.replace(/(\sbd\.\s)/ig, ' boulevard ');
        // street = street.replace(/(\schau\.\s)/ig, ' chaussée ');
        // street = street.replace(/(\strav\.\s)/ig, ' traverse ');
        // street = street.replace(/(\spl\.\s)/ig, ' place ');
        // street = street.replace(/(\spass\.\s)/ig, ' passage ');
        // street = street.replace(/(\ssent\.\s)/ig, ' sentier ');
        // street = street.replace(/(\simp\.\s)/ig, ' impasse ');
        // street = street.replace(/(\sall\.\s)/ig, ' allée ');
        // street = street.replace(/(\schem\.\s)/ig, ' chemin ');
        // street = street.replace(/(\sham\.\s)/ig, ' hameau ');
        // street = street.replace(/(\sdom\.\s)/ig, ' domaine ');

        const leadingZeroRegExp: RegExp = new RegExp('^(0+)[0-9]+,?\\s[a-zA-Z]+');
        const leadingZeroMatches: string[] = leadingZeroRegExp.exec(street);
        if (leadingZeroMatches !== null && leadingZeroMatches.length === 2) {
            street = street.substring(leadingZeroMatches[1].length);
        }

        AddressUtil.STREET_TYPE.forEach((type) => {
            street = street.replace(new RegExp('([0-9]+)(' + String(type.long) + '\\s)', 'ig'), '$1 $2');
            street = street.replace(new RegExp('([0-9]+)(' + String(type.short) + '\\.?\\s)', 'ig'), '$1 ' + String(type.long) + ' ');
        });

        return street;
    }

    public static getZipCodeFromCity(city: string): string {
        if (isNullOrUndefined(city)) {
            return undefined;
        }

        return Object.keys(AddressUtil.ZIPCODE_CITY).reduce((selected, zipcode) => {
            if (AddressUtil.ZIPCODE_CITY[zipcode].find((name) => name.toLowerCase() === city.toLowerCase()) !== undefined) {
                return zipcode;
            }

            return selected;
        }, undefined);
    }

    public static getCityFromZipCode(zip: string, rawAddress: string): string {
        if (zip === undefined || rawAddress === undefined) {
            return undefined;
        }

        const candidates: string[] = AddressUtil.ZIPCODE_CITY[zip];
        if (candidates === undefined) {
            return undefined;
        }

        if (candidates.length === 1) {
            return candidates[0];
        }

        let city: string;
        rawAddress.split(',').forEach((part) => {
            city = candidates.find((candidate) => {
                return part.trim().toLowerCase() === candidate.trim().toLowerCase();
            });
        });
        if (city !== undefined) {
            return city;
        }

        return candidates[0];
    }

    public static isZipCode(zip: string): boolean {
        if (typeof zip !== 'string') {
            return false;
        }

        if (Object.keys(AddressUtil.ZIPCODE_CITY).includes(zip)) {
            return true;
        }

        return ValidatorUtil.isNumber(zip) && zip.length === 5 && !zip.includes('.') && !zip.includes('-');
    }

    public static parse(raw: string, isFormatted: boolean = true): { street?: string, zip?: string, city?: string, country?: string } {
        const address: { street?: string, zip?: string, city?: string, country?: string } = {};
        if (!isNullOrUndefined(raw)) {
            const rawAddressParts: string[] = raw.split(',');
            if (rawAddressParts.length >= 3 && isFormatted) {
                if (AddressUtil.COUNTRIES.find((c) => raw.toLowerCase().includes(' ' + c.toLowerCase())) === undefined) {
                    rawAddressParts.push('France');
                }

                let i: number = rawAddressParts.length - 1;
                address.country = rawAddressParts[i].trim();
                rawAddressParts.splice(i--, 1);
                if (rawAddressParts[i] !== undefined && rawAddressParts[i].trim().includes(' ')) {
                    const rawParts: string[] = rawAddressParts[i].trim().split(' ');
                    address.zip = rawParts[0] === undefined ? undefined : rawParts[0].trim();
                    rawParts.splice(0, 1);
                    address.city = rawParts.join(' ').trim();
                    rawAddressParts.splice(i--, 1);
                } else if (!isNaN(Number(rawAddressParts[i].trim()))) {
                    address.zip = rawAddressParts[i].trim();
                    rawAddressParts.splice(i--, 1);
                    if (i >= 2) {
                        address.city = rawAddressParts[i].trim();
                        rawAddressParts.splice(i--, 1);
                    }
                } else if (!isNaN(Number(rawAddressParts[i - 1].trim()))) {
                    address.city = rawAddressParts[i].trim();
                    rawAddressParts.splice(i--, 1);
                    address.zip = rawAddressParts[i].trim();
                    rawAddressParts.splice(i--, 1);
                } else {
                    address.city = rawAddressParts[i].trim();
                    rawAddressParts.splice(i--, 1);
                }
                address.street = rawAddressParts.map((part) => part.trim()).join(' ').trim();
            } else {
                let rawAddress: string = raw.toLowerCase();
                Object.keys(AddressUtil.ZIPCODE_CITY).forEach((zipcode) => {
                    if (rawAddress.includes(zipcode)) {
                        address.zip = zipcode;
                        try {
                            rawAddress = rawAddress.replace(new RegExp(StringUtil.escapeRegExp(zipcode), 'ig'), '');
                        } catch (_e) {
                            // ignore
                        }
                    }
                });
                Object.values(AddressUtil.ZIPCODE_CITY).flat().forEach((city) => {
                    if (rawAddress.includes(city.toLowerCase()) && (address.zip === undefined || AddressUtil.ZIPCODE_CITY[address.zip]?.find((c) => c.toLowerCase() === city.toLowerCase()))) {
                        address.city = city;
                        try {
                            rawAddress = rawAddress.replace(new RegExp(StringUtil.escapeRegExp(city), 'ig'), '');
                        } catch (_e) {
                            // ignore
                        }
                    }
                });
                AddressUtil.COUNTRIES.forEach((country) => {
                    if (rawAddress.includes(' ' + country.toLowerCase()) && !rawAddress.includes('de ' + country.toLowerCase()) && !rawAddress.includes('de la ' + country.toLowerCase())) {
                        address.country = country;
                        try {
                            rawAddress = rawAddress.replace(new RegExp(StringUtil.escapeRegExp(' ' + country), 'ig'), '');
                        } catch (_e) {
                            // ignore
                        }
                    }
                });

                if (address.street === undefined) {
                    address.street = trim(rawAddress.split(',').map((part) => part.trim()).join(' '), '-').trim();
                }
            }

            if (address.country === undefined) {
                address.country = 'France';
            }
        }

        if (address.street !== undefined) {
            address.street = FormatterUtil.lower(FormatterUtil.trim(String(address.street)));
        }
        if (address.zip !== undefined) {
            address.zip = FormatterUtil.upper(FormatterUtil.trim(String(address.zip)));
        }
        if (address.city !== undefined) {
            address.city = AddressUtil.autoCorrectCity(FormatterUtil.capitalize(FormatterUtil.trim(String(address.city))));
        }
        if (address.country !== undefined) {
            address.country = FormatterUtil.capitalize(FormatterUtil.trim(String(address.country)));
        }

        return AddressUtil.complete(address);
    }

    public static complete(address: { street?: string, zip?: string, city?: string, country?: string }): { street?: string, zip?: string, city?: string, country?: string } {
        if (address.city === undefined || address.city.length === 0) {
            const city: string = AddressUtil.getCityFromZipCode(address.zip, address.street);
            if (city !== undefined) {
                address.city = city;
            }
        }
        if (typeof address.street === 'string') {
            AddressUtil.REGIONS.forEach((region) => {
                address.street = StringUtil.removeText(address.street, region).trim();
            });
        }
        if (FormatterUtil.lower(address.country) === 'fr') {
            address.country = 'France';
        }

        return address;
    }

    public static removeDuplicates(str: string): string {
        if (typeof str !== 'string') {
            return str;
        }

        const output: string[] = [];

        str.split(',').forEach((part) => {
            if (output.find((o) => o.trim().toLowerCase().includes(part.trim().toLowerCase())) === undefined) {
                output.push(part);
            }
        });

        return output.join(',');
    }

    private static countDiff(str: string, reference: string): { add: number, remove: number } {
        const result: { add: number, remove: number } = {add: 0, remove: 0};

        diff(str.toLowerCase(), reference.toLowerCase()).forEach((value) => {
            switch (value[0]) {
                case -1:
                    result.remove += value[1].length;
                    break;
                case 1:
                    result.add += value[1].length;
                    break;
            }
        });

        return result;
    }
}
